import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Header from './Header';
import Footer from './Footer';
import './AdminUpload.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001/lb-api';

const AdminPracticeUpload = ({ user }) => {
  const [excelFile, setExcelFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [practice, setPractice] = useState([]);
  const [filterSkill, setFilterSkill] = useState('');
  const [filterLang, setFilterLang] = useState('');
  const [filterLevel, setFilterLevel] = useState('');
  const [loadingPractice, setLoadingPractice] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const skills = ['Reading', 'Writing', 'Listening', 'Speaking'];

  const languages = [
    { code: 'German', name: 'German' },
    { code: 'French', name: 'French' },
    { code: 'Spanish', name: 'Spanish' },
    { code: 'Italian', name: 'Italian' },
    { code: 'Japanese', name: 'Japanese' },
    { code: 'English', name: 'English' }
  ];

  const levels = [
    { code: 'A1', name: 'A1' },
    { code: 'A2', name: 'A2' },
    { code: 'B1', name: 'B1' },
    { code: 'B2', name: 'B2' },
    { code: 'C1', name: 'C1' },
    { code: 'C2', name: 'C2' }
  ];

  const handleUpload = async () => {
    if (!excelFile) { setError('Please select an Excel file'); return; }
    setUploading(true);
    setError('');
    setResult(null);

    const formData = new FormData();
    formData.append('excel', excelFile);

    try {
      const res = await axios.post(`${API_BASE}/practice-prompts/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000
      });
      setResult(res.data);
      if (filterSkill || filterLang || filterLevel) {
        fetchPractice();
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
    setUploading(false);
  };

  const fetchPractice = async () => {
    setLoadingPractice(true);
    try {
      const params = new URLSearchParams();
      if (filterSkill) params.append('skill', filterSkill);
      if (filterLang) params.append('language', filterLang);
      if (filterLevel) params.append('proficiency_level', filterLevel);
      const res = await axios.get(`${API_BASE}/practice-prompts/list?${params.toString()}`, { withCredentials: true });
      setPractice(res.data.practice || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch practice data');
    }
    setLoadingPractice(false);
  };

  const handleDelete = async () => {
    if (!filterSkill && !filterLang && !filterLevel) {
      setError('Please select at least one filter to delete');
      return;
    }
    setDeleting(true);
    try {
      const res = await axios.post(`${API_BASE}/practice-prompts/delete`, {
        skill: filterSkill || undefined,
        language: filterLang || undefined,
        proficiency_level: filterLevel || undefined
      }, { withCredentials: true });
      setResult({ message: `Deleted ${res.data.deleted} practice entries.`, deleted: res.data.deleted });
      setPractice([]);
      setDeleteConfirm(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Delete failed');
    }
    setDeleting(false);
  };

  useEffect(() => {
    if (filterSkill || filterLang || filterLevel) {
      fetchPractice();
    } else {
      setPractice([]);
    }
  }, [filterSkill, filterLang, filterLevel]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="admin-upload-page">
      <Header user={user} />
      <main className="admin-upload-main">
        <h1>Practice Prompts Upload</h1>
        <p className="admin-upload-desc">Upload an Excel file to add or update practice prompts (Reading, Writing, Listening, Speaking).</p>

        <div className="admin-upload-section">
          <h3>Excel File Format</h3>
          <p className="admin-upload-hint">
            Required columns: <strong>Skill</strong> (Reading, Writing, Listening, Speaking), <strong>Language</strong> (German, French, etc.), 
            <strong> Level</strong> (A1, A2, B1, etc.), <strong>Prompt</strong> (one prompt per row)
          </p>
          <p className="admin-upload-hint" style={{ marginTop: '8px', color: '#555', fontStyle: 'italic' }}>
            Rows with the same Skill + Language + Level are grouped together. Each row's Prompt becomes an entry in the prompts JSON array.
          </p>
          <div style={{ marginTop: '12px' }}>
            <input type="file" accept=".xlsx,.xls,.csv" onChange={e => { setExcelFile(e.target.files[0]); setResult(null); setError(''); }} />
          </div>
        </div>

        <button className="admin-upload-btn" onClick={handleUpload} disabled={uploading || !excelFile}>
          {uploading ? 'Uploading...' : 'Upload Practice Prompts'}
        </button>

        {error && <div className="admin-upload-error">{error}</div>}
        {result && (
          <div className="admin-upload-result">
            <h3>✅ {result.message || 'Upload Complete'}</h3>
            {result.inserted !== undefined && <p>Inserted: <strong>{result.inserted}</strong></p>}
            {result.updated !== undefined && <p>Updated: <strong>{result.updated}</strong></p>}
            {result.skipped !== undefined && result.skipped > 0 && <p>Skipped: <strong>{result.skipped}</strong></p>}
            {result.deleted !== undefined && <p>Deleted: <strong>{result.deleted}</strong></p>}
            {result.errors && result.errors.length > 0 && (
              <div className="admin-upload-errors">
                <h4>Errors:</h4>
                {result.errors.map((e, i) => <p key={i}>{e}</p>)}
              </div>
            )}
          </div>
        )}

        <hr style={{ margin: '32px 0', borderColor: '#E0E0E0' }} />

        <h2 style={{ fontFamily: 'Graphik-Semibold', fontSize: '20px', color: '#333', marginBottom: '16px' }}>View / Delete Existing Practice Prompts</h2>
        
        <div className="admin-upload-section" style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
          <select value={filterSkill} onChange={e => setFilterSkill(e.target.value)} style={{ padding: '8px 12px', fontSize: '14px', border: '1px solid #CFCFCF' }}>
            <option value="">All Skills</option>
            {skills.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={filterLang} onChange={e => setFilterLang(e.target.value)} style={{ padding: '8px 12px', fontSize: '14px', border: '1px solid #CFCFCF' }}>
            <option value="">All Languages</option>
            {languages.map(l => <option key={l.code} value={l.code}>{l.name}</option>)}
          </select>
          <select value={filterLevel} onChange={e => setFilterLevel(e.target.value)} style={{ padding: '8px 12px', fontSize: '14px', border: '1px solid #CFCFCF' }}>
            <option value="">All Levels</option>
            {levels.map(l => <option key={l.code} value={l.code}>{l.name}</option>)}
          </select>
          <button onClick={fetchPractice} style={{ padding: '8px 16px', background: '#7500C0', color: '#fff', border: 'none', cursor: 'pointer', fontFamily: 'Graphik-Semibold', fontSize: '14px' }}>
            {loadingPractice ? 'Loading...' : 'Refresh'}
          </button>
          {(filterSkill || filterLang || filterLevel) && practice.length > 0 && (
            <>
              {!deleteConfirm ? (
                <button onClick={() => setDeleteConfirm(true)} style={{ padding: '8px 16px', background: '#DC2626', color: '#fff', border: 'none', cursor: 'pointer', fontFamily: 'Graphik-Semibold', fontSize: '14px' }}>
                  Delete Filtered
                </button>
              ) : (
                <span style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <span style={{ color: '#DC2626', fontSize: '13px' }}>Delete {practice.length} entries?</span>
                  <button onClick={handleDelete} disabled={deleting} style={{ padding: '6px 12px', background: '#DC2626', color: '#fff', border: 'none', cursor: 'pointer', fontSize: '13px' }}>
                    {deleting ? 'Deleting...' : 'Confirm'}
                  </button>
                  <button onClick={() => setDeleteConfirm(false)} style={{ padding: '6px 12px', background: '#888', color: '#fff', border: 'none', cursor: 'pointer', fontSize: '13px' }}>
                    Cancel
                  </button>
                </span>
              )}
            </>
          )}
        </div>

        {practice.length > 0 && (
          <div style={{ marginTop: '16px', overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'Graphik', fontSize: '13px' }}>
              <thead>
                <tr style={{ background: '#F1F1EF', textAlign: 'left' }}>
                  <th style={{ padding: '10px 8px', borderBottom: '1px solid #CFCFCF' }}>ID</th>
                  <th style={{ padding: '10px 8px', borderBottom: '1px solid #CFCFCF' }}>Skill</th>
                  <th style={{ padding: '10px 8px', borderBottom: '1px solid #CFCFCF' }}>Language</th>
                  <th style={{ padding: '10px 8px', borderBottom: '1px solid #CFCFCF' }}>Level</th>
                  <th style={{ padding: '10px 8px', borderBottom: '1px solid #CFCFCF' }}>Prompts</th>
                  <th style={{ padding: '10px 8px', borderBottom: '1px solid #CFCFCF' }}>Added By</th>
                </tr>
              </thead>
              <tbody>
                {practice.map(p => (
                  <tr key={p.id} style={{ borderBottom: '1px solid #E0E0E0' }}>
                    <td style={{ padding: '8px' }}>{p.id}</td>
                    <td style={{ padding: '8px', fontWeight: '500' }}>{p.skill}</td>
                    <td style={{ padding: '8px' }}>{p.language}</td>
                    <td style={{ padding: '8px' }}>{p.proficiency_level}</td>
                    <td style={{ padding: '8px', fontSize: '12px', maxWidth: '400px' }}>
                      {Array.isArray(p.prompts) ? (
                        <ul style={{ margin: 0, paddingLeft: '16px' }}>
                          {p.prompts.map((pr, i) => <li key={i}>{pr.text}</li>)}
                        </ul>
                      ) : p.prompts?.length + ' prompts'}
                    </td>
                    <td style={{ padding: '8px', fontSize: '12px' }}>{p.added_by}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p style={{ marginTop: '8px', fontSize: '13px', color: '#808080' }}>Showing {practice.length} entries</p>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default AdminPracticeUpload;
